/**
 * Notes List - Display and manage user's saved notes
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import NoteEditor from './NoteEditor';
import HeaderNav from '../HeaderNav';

const NotesList = ({ user, onLogout, onSaveNote }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingNote, setEditingNote] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [tags, setTags] = useState([]);
  const [selectedTag, setSelectedTag] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [savedAdviceCount, setSavedAdviceCount] = useState(0);

  useEffect(() => {
    loadNotes();
    loadTags();
    loadSavedAdviceCount();
  }, [selectedTag, searchQuery]);

  const loadSavedAdviceCount = async () => {
    try {
      const res = await axios.get('/api/engagement/saved');
      setSavedAdviceCount((res.data?.saved_items || []).length);
    } catch {
      setSavedAdviceCount(0);
    }
  };

  const loadNotes = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const params = {};
      if (selectedTag) params.tags = selectedTag;
      if (searchQuery) params.search = searchQuery;

      const res = await axios.get('/api/notes/', {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      const all = res.data.notes || [];
      setNotes(all.filter((n) => n.snapshot_type !== 'advice_card'));
    } catch (err) {
      console.error('Error loading notes:', err);
      setError('Failed to load notes');
    } finally {
      setLoading(false);
    }
  };

  const loadTags = async () => {
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const res = await axios.get('/api/notes/tags/all', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTags(res.data.tags || []);
    } catch (err) {
      console.error('Error loading tags:', err);
    }
  };

  const handleDelete = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note?')) return;

    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      await axios.delete(`/api/notes/${noteId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      loadNotes();
    } catch (err) {
      console.error('Error deleting note:', err);
      alert('Failed to delete note');
    }
  };

  const handleEdit = (note) => {
    setEditingNote(note);
    setShowEditor(true);
  };

  const handleEditorClose = () => {
    setEditingNote(null);
    setShowEditor(false);
    loadNotes();
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (loading && notes.length === 0) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 mx-auto mb-4 border-2 border-hairline border-t-pine rounded-full animate-spin" />
          <p className="text-body text-secondary">Loading notes…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone">
      {user && (
        <HeaderNav activeSection="notes" onSectionChange={() => {}} user={user} onLogout={onLogout} visionCount={0} />
      )}
      <div className="max-w-6xl mx-auto px-6 py-8 md:py-10">
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: 'easeOut' }}>
          <div className="flex flex-wrap items-end justify-between gap-4 mb-8 pb-6 border-b border-hairline">
            <div>
              <h1 className="font-display text-h1 text-primary mb-2">My notes</h1>
              <p className="text-body text-secondary">
                Manual notes from games, lessons, and freeform capture
              </p>
              {savedAdviceCount > 0 && (
                <Link
                  to="/my-plan"
                  className="inline-flex items-center gap-2 mt-3 text-label text-pine border border-pine rounded-md px-3 py-1.5 bg-pine-tint hover:bg-pine-tint/80 transition-colors duration-300"
                >
                  View {savedAdviceCount} saved advice item{savedAdviceCount === 1 ? '' : 's'} in My plan
                </Link>
              )}
            </div>
            <button
              type="button"
              onClick={() => {
                setEditingNote(null);
                setShowEditor(true);
              }}
              className="btn-primary"
            >
              New note
            </button>
          </div>

          <div className="flex flex-wrap gap-4 mb-8">
            <input
              type="text"
              placeholder="Search notes…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input-editorial flex-1 min-w-[200px]"
            />
            <select
              value={selectedTag || ''}
              onChange={(e) => setSelectedTag(e.target.value || null)}
              className="select-editorial min-w-[160px]"
            >
              <option value="">All tags</option>
              {tags.map((tag) => (
                <option key={tag} value={tag}>
                  {tag}
                </option>
              ))}
            </select>
          </div>
        </motion.div>

        {error ? (
          <div className="surface-panel p-4 border-danger/30">
            <p className="text-body text-danger">{error}</p>
          </div>
        ) : notes.length === 0 ? (
          <div className="text-center py-20 aura-card">
            <p className="text-body text-secondary mb-4">No notes yet</p>
            <button
              type="button"
              onClick={() => {
                setEditingNote(null);
                setShowEditor(true);
              }}
              className="btn-primary"
            >
              Create your first note
            </button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {notes.map((note, index) => (
              <motion.article
                key={note.note_id}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.04, duration: 0.4, ease: 'easeOut' }}
                className="aura-card cursor-pointer hover:bg-pine-tint/30 transition-colors duration-300"
                onClick={() => handleEdit(note)}
              >
                <h3 className="font-display text-h3 text-primary mb-2 truncate">{note.title}</h3>
                <p className="text-body text-secondary mb-4 line-clamp-3 leading-relaxed">{note.content}</p>

                {note.tags && note.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {note.tags.map((tag) => (
                      <span
                        key={tag}
                        className="text-label px-2 py-0.5 border border-hairline rounded-md text-secondary bg-pine-tint"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}

                <div className="flex items-center justify-between text-label text-faint pt-3 border-t border-hairline">
                  <span>{formatDate(note.created_at)}</span>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(note);
                      }}
                      className="text-pine hover:text-primary transition-colors duration-300"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(note.note_id);
                      }}
                      className="text-danger hover:opacity-80 transition-opacity duration-300"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        )}
      </div>

      {showEditor && (
        <NoteEditor note={editingNote} user={user} onClose={handleEditorClose} onSave={onSaveNote} />
      )}
    </div>
  );
};

export default NotesList;
